import { format } from 'date-fns';

import { useLocale } from 'hooks/useLocale/useLocale';
import { HistoryList } from 'ui/historyList/HistoryList';
import { useLocaleStoreQuery } from 'hooks/useLocaleStoreQuery/useLocaleStoreQuery';
import { filterQueryFromPagination } from 'helpers/filterQueryFromPagination';
import { AppMessages } from 'i18n/messages';
import { DateFormat } from 'core/global.enum';

import { UserDetailsHistoryFilters } from './userDetailsHistoryFilters/UserHistoryFilters';
import { UserDetailsHistoryProps } from './UserDetailsHistory.types';

export const UserDetailsHistory = ({
  historyResponse,
  isLoading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  userSearchQuery,
  setUserSearchQuery,
  onClearHistoryQuery,
}: UserDetailsHistoryProps) => {
  const { formatMessage } = useLocale();

  const {
    storedQueries: { userHistory: userHistoryQuery },
  } = useLocaleStoreQuery();

  const isClearFiltersButtonVisible =
    Object.values(filterQueryFromPagination(userHistoryQuery)).some((query) => query) || !!userSearchQuery;

  return (
    <HistoryList
      data={historyResponse?.data || []}
      isLoading={isLoading}
      filters={{
        isClearButtonVisible: isClearFiltersButtonVisible,
        onClearFilters: onClearHistoryQuery,
        render: () => (
          <UserDetailsHistoryFilters userSearchQuery={userSearchQuery} setUserSearchQuery={setUserSearchQuery} />
        ),
      }}
      pagination={{
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        count: historyResponse?.count || 0,
      }}
      renderHistoryItemBody={(history) => (
        <>
          {history.wykonawca && (
            <HistoryList.ActionBodyItem
              title={formatMessage({ id: AppMessages['user.history.executor'] })}
              value={`${history.wykonawca.nazwisko} ${history.wykonawca.imie}`}
            />
          )}

          {history.nazwa && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.name'] })}
              oldValue={history.nazwa.old}
              newValue={history.nazwa.new}
            />
          )}

          {history.status && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.status'] })}
              oldValue={history.status.old}
              newValue={history.status.new}
            />
          )}

          {history.waznoscDo && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.expirationDate'] })}
              oldValue={history.waznoscDo.old ? format(new Date(history.waznoscDo.old), DateFormat.FULL) : ''}
              newValue={history.waznoscDo.new ? format(new Date(history.waznoscDo.new), DateFormat.FULL) : ''}
            />
          )}

          {history.imie && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.firstName'] })}
              oldValue={history.imie.old}
              newValue={history.imie.new}
            />
          )}

          {history.nazwisko && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.lastName'] })}
              oldValue={history.nazwisko.old}
              newValue={history.nazwisko.new}
            />
          )}

          {history.email && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.email'] })}
              oldValue={history.email.old}
              newValue={history.email.new}
            />
          )}

          {history.telefon && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.phone'] })}
              oldValue={history.telefon.old}
              newValue={history.telefon.new}
            />
          )}

          {history.opis && (
            <HistoryList.ActionModifiedLongFieldBodyItem
              title={formatMessage({ id: AppMessages['user.history.description'] })}
              oldValue={history.opis.old}
              newValue={history.opis.new}
            />
          )}

          {history.haslo && (
            <HistoryList.ActionModifiedPinBodyItem
              title={formatMessage({ id: AppMessages['user.history.password'] })}
              oldValue={history.haslo.old}
              newValue={history.haslo.new}
            />
          )}

          {history.powod && (
            <HistoryList.ActionBodyItem
              title={formatMessage({ id: AppMessages['user.history.reason'] })}
              value={history.powod}
            />
          )}
        </>
      )}
    />
  );
};
